/* eslint-disable @typescript-eslint/no-unused-vars */
import { type GetListParams } from "react-admin";

import { createApiClient } from "@/api";
import { CLIENT_SEARCH_SOURCE } from "@/constants/filters";
import { type BaseListFilter } from "@/types/filters";
import { type DataProviderSchema, type GetEntityTypes } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

const apiClient = createApiClient("jsonld");

const BASE_URL = "/api/vpn/tariffs";

type TariffWrite = GetEntityTypes<"Tariff", "jsonld">["write"];

const fetchTariffsListData = async (params: GetListParams) => {
  const typedFilter = params.filter as BaseListFilter;
  const {
    [CLIENT_SEARCH_SOURCE]: _,
    per_page: _perPage,
    ...restFilters
  } = typedFilter;
  const { data } = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        page: params.pagination?.page,
        ...restFilters,
      },
    },
  });
  return {
    data: data?.["hydra:member"] ?? [],
    total: data?.["hydra:totalItems"] ?? 0,
  };
};

export const tariffsSchema: DataProviderSchema<"Tariff", "jsonld"> = {
  list: getLocalFilteredSortedList(fetchTariffsListData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("Tariff not found");

    return { data };
  },
  edit: async ({ id, data }) => {
    const editData = data as unknown as Omit<TariffWrite, "clientApp"> & {
      clientApp?: { "@id"?: string } | string | null;
    };
    const { clientApp, ...body } = editData;
    const clientAppId: string | null =
      typeof clientApp === "object" && clientApp !== null
        ? (clientApp["@id"] ?? null)
        : (clientApp ?? null);
    const { data: responseData } = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        ...body,
        clientApp: clientAppId,
        price: body.price ? String(body.price) : undefined,
      },
    });

    if (!responseData) throw Error("Something went wrong");

    return { data: responseData };
  },
  create: async ({ data: body }) => {
    const formattedBody = {
      ...body,
      price: body.price ? String(body.price) : undefined,
    } as TariffWrite;
    const { data } = await apiClient.POST(BASE_URL, {
      body: formattedBody,
    });

    if (!data) throw Error("Something went wrong");

    return {
      data,
    };
  },
};
